import { useMemo } from "react";
import { EyeIcon } from "@heroicons/react/20/solid";
import { UserIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import { Badge } from "@via/components/badge.tsx";
import { VmetaPermissionV2 } from "common";

interface PermissionBadgeProps {
  icon: typeof EyeIcon;
  count: number;
  bgColor: string;
  textColor: string;
  iconColor: string;
  permissions: VmetaPermissionV2;
  permissionType: "read" | "write" | "everyone";
  rowId?: string;
}

export const PermissionBadge = ({
  icon: Icon,
  count,
  bgColor,
  textColor,
  iconColor,
  permissions,
  permissionType,
  rowId = "",
}: PermissionBadgeProps) => {
  const tooltipId = useMemo(
    () => `permission-${permissionType}-${rowId || "default"}`,
    [permissionType, rowId]
  );

  const userPermissions = permissions.filter(
    perm => perm.type === "user"
  ) as Array<{ type: "user"; email?: string; username?: string }>;

  const groupPermissions = permissions.filter(
    perm => perm.type === "group"
  ) as Array<{ type: "group"; name?: string }>;

  const hasEveryone = permissions.some(perm => perm.type === "everyone");

  const hasDetails =
    userPermissions.length > 0 || groupPermissions.length > 0 || hasEveryone;

  // Determine badge colors based on permission type
  const badgeTextColor =
    permissionType === "read" ? "text-blue-600" : "text-green-600";
  const badgeFillColor =
    permissionType === "read" ? "fill-blue-600" : "fill-green-600";

  return (
    <>
      <div
        id={tooltipId}
        className={`flex items-center gap-2 ${bgColor} p-2 rounded-md w-fit cursor-pointer hover:opacity-80 transition-opacity`}>
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className={`text-sm font-medium ${textColor}`}>{count}</span>
      </div>

      {hasDetails && (
        <Tooltip
          anchorSelect={`#${tooltipId}`}
          place="bottom"
          className="z-50 !bg-white !text-gray-900 !shadow-lg !ring-1 !ring-black !ring-opacity-5 !rounded-lg !p-0 !max-w-xs"
          noArrow={false}
          clickable>
          <div className="p-3 w-full">
            <div className="text-xs font-semibold text-gray-900 mb-2 uppercase">
              {permissionType} Permissions
            </div>

            {hasEveryone && (
              <div className="mb-3 pb-3 border-b border-gray-200">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  Public Access
                </div>
              </div>
            )}

            {userPermissions.length > 0 && (
              <div className="mb-3 pb-3 border-b border-gray-200">
                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-2">
                  <UserIcon className="w-4 h-4" />
                  Users ({userPermissions.length})
                </div>
                <div className="flex flex-col gap-1.5 max-h-32 overflow-y-auto">
                  {userPermissions.map((user, index) => (
                    <Badge
                      key={`user-${user?.email || user?.username || index}`}
                      text={user?.email || user?.username || "Unknown user"}
                      textColor={badgeTextColor}
                      fillColor={badgeFillColor}
                      maxLength={100}
                    />
                  ))}
                </div>
              </div>
            )}

            {groupPermissions.length > 0 && (
              <div>
                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-2">
                  <UserGroupIcon className="w-4 h-4" />
                  Groups ({groupPermissions.length})
                </div>
                <div className="flex flex-col gap-1.5 max-h-32 overflow-y-auto">
                  {groupPermissions.map((group, index) => (
                    <Badge
                      key={`group-${group?.name || index}`}
                      text={group?.name || "Unknown group"}
                      textColor={badgeTextColor}
                      fillColor={badgeFillColor}
                      maxLength={100}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </Tooltip>
      )}
    </>
  );
};
